import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtMoney } from "@/components/badges";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const EMPTY_FORM = { kind: "ipc", amount: "", currency: "USD", description: "", status: "draft", date: "", file: null };

export function EditPaymentDialog({ rec, onClose, clientName }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { selectedId } = useProject();
  const [editForm, setEditForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (rec) setEditForm({ kind: rec.kind, amount: rec.amount, currency: rec.currency || "USD", description: rec.description || "", status: rec.status === "approved" ? "sent" : rec.status, date: rec.date || "", file: null });
  }, [rec]);

  const updateMut = useMutation({
    mutationFn: async ({ id, body }) => api.put(`/payments/${id}`, body),
    onSuccess: (r) => {
      if (r?.data?.pending) {
        toast.success(t("payments.toastPending"));
        qc.invalidateQueries({ queryKey: ["payment-actions", selectedId] });
      } else {
        toast.success(t("payments.toastUpdated"));
      }
      onClose();
      qc.invalidateQueries({ queryKey: ["payments", selectedId] });
      qc.invalidateQueries({ queryKey: ["dashboard", selectedId] });
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  return (
    <Dialog open={!!rec} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-payment-dialog">
        <DialogHeader><DialogTitle>{t("payments.editTitle")} — {fmtMoney(rec?.amount || 0, rec?.currency)}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t("payments.thType")}</Label>
            <Select value={editForm.kind} onValueChange={(v) => setEditForm({ ...editForm, kind: v })}>
              <SelectTrigger data-testid="edit-payment-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ipc" data-testid="edit-payment-kind-ipc">{t("payments.kindIpcLong", { client: clientName })}</SelectItem>
                <SelectItem value="payment" data-testid="edit-payment-kind-payment">{t("payments.kindPaymentLong", { client: clientName })}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t("payments.amountL")}</Label>
              <Input type="number" min="0" step="0.01" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} className="font-mono" data-testid="edit-payment-amount-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("payments.currencyL")}</Label>
              <Select value={editForm.currency} onValueChange={(v) => setEditForm({ ...editForm, currency: v })}>
                <SelectTrigger data-testid="edit-payment-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["AED", "USD", "EUR", "TRY"].map((c) => (
                    <SelectItem key={c} value={c} data-testid={`edit-payment-currency-${c}`}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("common.description")}</Label>
            <Textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} data-testid="edit-payment-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t("common.status")}</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger data-testid="edit-payment-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft" data-testid="edit-payment-status-draft">{t("badges.draft")}</SelectItem>
                  <SelectItem value="sent" data-testid="edit-payment-status-sent">{t("badges.sent")}</SelectItem>
                  <SelectItem value="paid" data-testid="edit-payment-status-paid">{t("badges.paid")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("payments.dateL")}</Label>
              <Input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} data-testid="edit-payment-date-input" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={() => updateMut.mutate({ id: rec.id, body: { kind: editForm.kind, amount: Number(editForm.amount), currency: editForm.currency, description: editForm.description, status: editForm.status, date: editForm.date || null } })}
            disabled={!editForm.amount || updateMut.isPending}
            data-testid="edit-payment-submit-button"
          >
            {updateMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("common.saveChanges")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
